/**
 * WebSocket Service for Web Dashboard
 * Migrated from Socket.io to Native WebSocket for AWS API Gateway compatibility
 */

export interface WebSocketConfig {
  url: string;
  token: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
  debug?: boolean;
}

export interface WebSocketMessage {
  action: string;
  data?: any;
  [key: string]: any;
}

export type WebSocketStatus = 'connecting' | 'connected' | 'disconnecting' | 'disconnected' | 'error';

export interface WebSocketCallbacks {
  onOpen?: () => void;
  onMessage?: (message: WebSocketMessage) => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (error: Event) => void;
  onStatusChange?: (status: WebSocketStatus) => void;
}

const DEFAULT_CONFIG = {
  reconnectInterval: 5000,
  maxReconnectAttempts: 5,
  heartbeatInterval: 30000,
  debug: process.env.NODE_ENV === 'development',
};

export class WebSocketService {
  private ws: WebSocket | null = null;
  private config: Required<Omit<WebSocketConfig, 'token'>> & { token: string };
  private callbacks: WebSocketCallbacks;
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimeout: ReturnType<typeof setInterval> | null = null;
  private status: WebSocketStatus = 'disconnected';
  private messageQueue: WebSocketMessage[] = [];
  private isIntentionalClose = false;

  constructor(config: WebSocketConfig, callbacks: WebSocketCallbacks = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.callbacks = callbacks;
  }

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.log('Already connected');
      return;
    }

    if (this.ws?.readyState === WebSocket.CONNECTING) {
      this.log('Already connecting');
      return;
    }

    this.isIntentionalClose = false;
    this.setStatus('connecting');

    // Add token to URL query parameters
    const url = `${this.config.url}?token=${encodeURIComponent(this.config.token)}`;
    this.log('Connecting to', url.replace(/token=[^&]+/, 'token=***'));

    try {
      this.ws = new WebSocket(url);
      this.setupEventHandlers();
    } catch (error) {
      this.error('Connection error:', error);
      this.setStatus('error');
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.isIntentionalClose = true;
    this.clearTimeouts();

    if (this.ws) {
      this.setStatus('disconnecting');
      this.log('Disconnecting...');
      this.ws.close(1000, 'Client disconnected');
      this.ws = null;
    }

    this.setStatus('disconnected');
  }

  /**
   * Send message to server
   */
  send(message: WebSocketMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(message));
        this.log('Message sent:', message.action);
      } catch (error) {
        this.error('Failed to send message:', error);
      }
    } else {
      this.log('WebSocket not connected, queuing message');
      this.messageQueue.push(message);
    }
  }

  /**
   * Get current connection status
   */
  getStatus(): WebSocketStatus {
    return this.status;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.log('Connection opened');
      this.setStatus('connected');
      this.reconnectAttempts = 0;

      // Send queued messages
      while (this.messageQueue.length > 0) {
        const message = this.messageQueue.shift();
        if (message) {
          this.send(message);
        }
      }

      // Start heartbeat
      this.startHeartbeat();

      // Call callback
      this.callbacks.onOpen?.();
    };

    this.ws.onmessage = (event) => {
      this.log('Message received:', event.data);

      try {
        const message = JSON.parse(event.data) as WebSocketMessage;
        this.callbacks.onMessage?.(message);
      } catch (error) {
        this.error('Failed to parse message:', error);
      }
    };

    this.ws.onclose = (event) => {
      this.log('Connection closed', event.code, event.reason);
      this.clearTimeouts();

      if (!this.isIntentionalClose) {
        this.setStatus('disconnected');
        this.scheduleReconnect();
      } else {
        this.setStatus('disconnected');
      }

      // Call callback
      this.callbacks.onClose?.(event);
    };

    this.ws.onerror = (event) => {
      this.error('WebSocket error:', event);
      this.setStatus('error');

      // Call callback
      this.callbacks.onError?.(event);
    };
  }

  /**
   * Schedule reconnection attempt with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      this.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    // Exponential backoff: 5s → 10s → 20s → 40s → 60s (max)
    const delay = Math.min(
      this.config.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1),
      60000 // Max 60 seconds between attempts
    );

    this.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.config.maxReconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Start heartbeat to keep connection alive
   */
  private startHeartbeat(): void {
    this.clearHeartbeat();

    this.heartbeatTimeout = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({ action: 'ping' });
      }
    }, this.config.heartbeatInterval);
  }

  /**
   * Clear heartbeat timer
   */
  private clearHeartbeat(): void {
    if (this.heartbeatTimeout) {
      clearInterval(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  /**
   * Clear all timeouts
   */
  private clearTimeouts(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.clearHeartbeat();
  }

  /**
   * Set connection status and notify
   */
  private setStatus(status: WebSocketStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.log('Status changed:', status);
      this.callbacks.onStatusChange?.(status);
    }
  }

  /**
   * Log helper
   */
  private log(...args: unknown[]): void {
    if (this.config.debug) {
      // Using native console.debug for WebSocket as it's already controlled by debug flag
      console.debug('[WebSocket]', ...args);
    }
  }

  /**
   * Error helper
   */
  private error(...args: any[]): void {
    console.error('[WebSocket]', ...args);
  }
}

/**
 * Create WebSocket URL with query parameters
 */
export function createWebSocketUrl(baseUrl: string, params?: Record<string, string>): string {
  const url = new URL(baseUrl);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });
  }

  return url.toString();
}
